import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Callout } from '@/components/patterns/callout';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { dataSource } from '@/data';
import { ehAcessoNegado } from '@/data/http';
import { usePodeVer } from '@/hooks/use-acesso';
import { useEventos } from '@/hooks/use-eventos';
import { useResource } from '@/hooks/use-resource';
import { formatarData } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * Resumo (`GET /v1/resumo`) em formato compacto: linhas rótulo/valor na coluna
 * lateral do desktop e grade de 2 colunas no celular. "Incidentes abertos" é
 * um link para a lista já filtrada (aberto + mitigado). Atualiza sozinho
 * quando algo muda em outra tela (status, painel, incidente).
 */
export function ResumoLateral({ className, faixa = false }: { className?: string; faixa?: boolean }) {
  const verIncidentes = usePodeVer('incidentes');
  const verPainel = usePodeVer('painel');
  const resumo = useResource((signal) => dataSource.resumo(signal), []);
  useEventos(() => resumo.atualizarEmSilencio());

  if (resumo.estado === 'erro') {
    // 403: o resumo simplesmente não existe para este perfil — sem erro vermelho.
    if (ehAcessoNegado(resumo.erro)) return null;
    return (
      <Callout
        tom="erro"
        acao={
          <Button size="sm" variant="outline" onClick={resumo.recarregar}>
            Tentar de novo
          </Button>
        }
      >
        Não foi possível carregar o resumo: {resumo.erro?.message}
      </Callout>
    );
  }

  const dados = resumo.dados;
  if (!dados) {
    return (
      <div role="status" aria-label="Carregando o resumo">
        <Skeleton className={faixa ? 'h-[4.25rem] rounded-card' : 'h-40 rounded-card lg:h-56'} />
      </div>
    );
  }

  const incidentes = dados.incidentes_abertos;
  const celula = faixa
    ? 'flex min-w-0 flex-col gap-0.5 bg-bg-surface px-4 py-3'
    : 'flex flex-col gap-0.5 bg-bg-surface px-3 py-2.5 lg:px-3.5 lg:py-3 lg:flex-row lg:items-baseline lg:justify-between lg:gap-3';
  const linkIncidentes = faixa
    ? 'group flex w-full min-w-0 flex-col gap-0.5 px-4 py-3 transition-colors duration-fast ease-standard hover:bg-primary-wash'
    : 'group flex w-full flex-col gap-0.5 px-3 py-2.5 lg:px-3.5 lg:py-3 transition-colors duration-fast ease-standard hover:bg-primary-wash lg:flex-row lg:items-baseline lg:justify-between lg:gap-3';

  return (
    <section aria-labelledby="resumo-titulo" className={cn('overflow-hidden rounded-card border border-border bg-bg-surface shadow-sm', className)}>
      <h2 id="resumo-titulo" className="sr-only">
        Resumo das versões
      </h2>
      <dl
        className={cn(
          'grid gap-px bg-border-subtle',
          faixa ? 'grid-cols-2 sm:grid-cols-3 lg:grid-flow-col lg:auto-cols-fr' : 'grid-cols-3 lg:grid-cols-1',
        )}
      >
        <Linha rotulo="Versões publicadas" className={cn(celula, !faixa && 'hidden lg:flex')}>
          {dados.total_builds.toLocaleString('pt-BR')}
        </Linha>
        <Linha rotulo="Últimas 24h" className={celula}>
          {dados.ultimas_24h.toLocaleString('pt-BR')}
        </Linha>
        <Linha rotulo="Não homologadas" className={celula}>
          {dados.nao_homologadas.toLocaleString('pt-BR')}
        </Linha>
        {verIncidentes ? (
          <div className={cn(celula, 'p-0 lg:p-0')}>
            <Link
              to="/incidentes?status=aberto&status=mitigado"
              aria-label={`Incidentes abertos: ${incidentes}. Ver a lista.`}
              className={linkIncidentes}
            >
              <dt className="cmp-eyebrow">Incidentes abertos</dt>
              <dd
                className={cn(
                  'flex items-center gap-1 font-narrow text-card-title tabular-nums',
                  incidentes > 0 ? 'text-danger' : 'text-on-surface',
                )}
              >
                {incidentes.toLocaleString('pt-BR')}
                {faixa ? null : (
                  <ChevronRight className="hidden size-4 text-icon transition-transform duration-fast ease-standard group-hover:translate-x-0.5 lg:block" aria-hidden="true" />
                )}
              </dd>
            </Link>
          </div>
        ) : null}
        {verPainel ? (
          <div
            className={cn(
              celula,
              faixa ? 'col-span-2 sm:col-span-3 lg:col-span-1' : 'col-span-3 lg:col-span-1 lg:flex-col lg:items-start lg:gap-0.5',
            )}
          >
            <dt className="cmp-eyebrow">No painel do cliente</dt>
            <dd className="flex flex-wrap items-baseline gap-x-2">
              {dados.painel.versao ? (
                <span className="font-mono text-body font-bold">{dados.painel.versao}</span>
              ) : (
                <span className="text-body text-on-surface-muted">Nenhuma versão publicada</span>
              )}
              {dados.painel.desde ? (
                <span className="text-cell font-normal text-on-surface-muted">desde {formatarData(dados.painel.desde)}</span>
              ) : null}
            </dd>
          </div>
        ) : null}
      </dl>
    </section>
  );
}

function Linha({ rotulo, children, className }: { rotulo: string; children: React.ReactNode; className: string }) {
  return (
    <div className={className}>
      <dt className="cmp-eyebrow">{rotulo}</dt>
      <dd className="font-narrow text-card-title tabular-nums">{children}</dd>
    </div>
  );
}
